import type { RadioButtonGroupProps } from '@hover/blueprint';
import { RadioButton, RadioButtonGroup } from '@hover/blueprint';
import React from 'react';

import { FeatureFlag } from 'src/shared/constants';
import { useTreatment } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import { useHomeownerAppContext } from '../../hooks';
import { formatSpaceType } from '../Utils';

const DEFAULT_SPACE_TYPES = [
  'exterior',
  'bedroom',
  'bathroom',
  'dining_room',
  'home_office',
  'kitchen',
  'living_room',
];

export interface SpaceTypeSelectorProps
  extends Omit<RadioButtonGroupProps, 'children'> {
  spaceType: string;
  spaceTypes?: string[];
  setSpaceType: (type: string) => void;
}

const SpaceTypeSelector = ({
  spaceType,
  spaceTypes = [],
  setSpaceType,
  onChange,
  ...props
}: SpaceTypeSelectorProps) => {
  const { trackingData } = useHomeownerAppContext();
  /**
   * If the configuration split is off AND there are no space types passed in,
   * use the default space types. In all other cases, use the space types passed in as an argument.
   * This logic can be removed in favor of always using the space types passed in as an argument
   * (including the loading state if no space types exist) once the config split is fully rolled out.
   * */
  const isConfigSplitOn = useTreatment(FeatureFlag.Web.CLIENT_CONFIG);
  const displaySpaceTypes =
    !isConfigSplitOn && spaceTypes.length === 0
      ? DEFAULT_SPACE_TYPES
      : spaceTypes;

  return (
    <RadioButtonGroup
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      gap={200}
      onChange={type => {
        buttonPressed({
          ...trackingData,
          track_id: 'upload-image-space-type-selection',
          button_location: 'middle-main-page',
          button_text: type,
        });

        setSpaceType(type);
        onChange?.(type);
      }}
      value={spaceType}
      {...props}
    >
      {displaySpaceTypes.map(type => (
        <RadioButton
          key={type}
          size={{ base: 'small', tablet: 'medium' }}
          value={type}
        >
          {formatSpaceType(type)}
        </RadioButton>
      ))}
      {displaySpaceTypes.length === 0 && (
        <RadioButton isDisabled isLoading width={600} />
      )}
    </RadioButtonGroup>
  );
};

export { SpaceTypeSelector };
